import Phaser from 'phaser';
import type { MapData } from '@/types/map-data';
import { TILE_SIZE } from '@/config/game-config';
import { COLORS } from '@/config/colors';

const HUD_OFFSET_Y = 48;

interface DotSprite {
  readonly graphics: Phaser.GameObjects.Arc;
  readonly glow: Phaser.GameObjects.Arc;
  readonly tileX: number;
  readonly tileY: number;
  eaten: boolean;
}

interface PowerPillSprite {
  readonly graphics: Phaser.GameObjects.Arc;
  readonly glow: Phaser.GameObjects.Arc;
  readonly tileX: number;
  readonly tileY: number;
  eaten: boolean;
}

export interface RenderedMap {
  readonly wallGraphics: Phaser.GameObjects.Graphics;
  readonly dots: DotSprite[];
  readonly powerPills: PowerPillSprite[];
  readonly totalDots: number;
  readonly powerPillTweens: Phaser.Tweens.Tween[];
}

export function tileToPixelX(tileX: number): number {
  return tileX * TILE_SIZE + TILE_SIZE / 2;
}

export function tileToPixelY(tileY: number): number {
  return tileY * TILE_SIZE + TILE_SIZE / 2 + HUD_OFFSET_Y;
}

export function pixelToTileX(pixelX: number): number {
  return Math.floor(pixelX / TILE_SIZE);
}

export function pixelToTileY(pixelY: number): number {
  return Math.floor((pixelY - HUD_OFFSET_Y) / TILE_SIZE);
}

export function isWalkable(mapData: MapData, tileX: number, tileY: number): boolean {
  if (tileX < 0 || tileX >= mapData.width || tileY < 0 || tileY >= mapData.height) {
    return false;
  }
  const row = mapData.tiles[tileY];
  if (!row) {
    return false;
  }
  const tile = row[tileX];
  return tile !== 'wall';
}

/** Check if a tile at (x, y) is a wall */
function isWallTile(mapData: MapData, x: number, y: number): boolean {
  if (x < 0 || x >= mapData.width || y < 0 || y >= mapData.height) {
    return true; // Treat out-of-bounds as walls for edge rendering
  }
  const row = mapData.tiles[y];
  if (!row) return true;
  return row[x] === 'wall';
}

export function renderMap(scene: Phaser.Scene, mapData: MapData): RenderedMap {
  const wallGraphics = scene.add.graphics();
  const dots: DotSprite[] = [];
  const powerPills: PowerPillSprite[] = [];

  // Render walls with neon glow style
  renderNeonWalls(wallGraphics, mapData);

  // Render dots and power pills
  for (let y = 0; y < mapData.height; y++) {
    const row = mapData.tiles[y];
    if (!row) continue;
    for (let x = 0; x < mapData.width; x++) {
      const tile = row[x];
      if (!tile) continue;
      if (tile === 'dot') {
        dots.push(createDotSprite(scene, tileToPixelX(x), tileToPixelY(y), x, y));
      } else if (tile === 'power_pill') {
        powerPills.push(createPowerPillSprite(scene, tileToPixelX(x), tileToPixelY(y), x, y));
      } else if (tile === 'ghost_base') {
        renderGhostBaseTile(wallGraphics, tileToPixelX(x), tileToPixelY(y));
      }
    }
  }

  // Render tunnel indicators
  renderTunnelIndicators(scene, wallGraphics, mapData);

  const totalDots = dots.length + powerPills.length;
  const powerPillTweens = startPowerPillPulsing(scene, powerPills);

  return { wallGraphics, dots, powerPills, totalDots, powerPillTweens };
}

/**
 * Render walls as neon outlined edges rather than filled blocks.
 * Draws glowing line segments along the borders between wall and non-wall tiles.
 */
function renderNeonWalls(
  graphics: Phaser.GameObjects.Graphics,
  mapData: MapData,
): void {
  // First pass: draw the outer glow layer (wider, more transparent)
  graphics.lineStyle(3, COLORS.MAZE_WALL_GLOW, 0.35);
  drawWallEdges(graphics, mapData);

  // Second pass: draw the bright core line
  graphics.lineStyle(1.5, COLORS.MAZE_WALL, 0.9);
  drawWallEdges(graphics, mapData);
}

/** Draw line segments at the border between wall and non-wall tiles */
function drawWallEdges(
  graphics: Phaser.GameObjects.Graphics,
  mapData: MapData,
): void {
  const half = TILE_SIZE / 2;

  for (let y = 0; y < mapData.height; y++) {
    const row = mapData.tiles[y];
    if (!row) continue;
    for (let x = 0; x < mapData.width; x++) {
      const tile = row[x];
      if (tile !== 'wall') continue;

      const px = tileToPixelX(x);
      const py = tileToPixelY(y);

      // For each wall tile, check each neighbor. If the neighbor is NOT a wall,
      // draw a line segment on that edge.
      // Top edge
      if (!isWallTile(mapData, x, y - 1)) {
        graphics.beginPath();
        graphics.moveTo(px - half, py - half);
        graphics.lineTo(px + half, py - half);
        graphics.strokePath();
      }
      // Bottom edge
      if (!isWallTile(mapData, x, y + 1)) {
        graphics.beginPath();
        graphics.moveTo(px - half, py + half);
        graphics.lineTo(px + half, py + half);
        graphics.strokePath();
      }
      // Left edge
      if (!isWallTile(mapData, x - 1, y)) {
        graphics.beginPath();
        graphics.moveTo(px - half, py - half);
        graphics.lineTo(px - half, py + half);
        graphics.strokePath();
      }
      // Right edge
      if (!isWallTile(mapData, x + 1, y)) {
        graphics.beginPath();
        graphics.moveTo(px + half, py - half);
        graphics.lineTo(px + half, py + half);
        graphics.strokePath();
      }
    }
  }
}

function renderGhostBaseTile(
  graphics: Phaser.GameObjects.Graphics,
  pixelX: number,
  pixelY: number,
): void {
  const half = TILE_SIZE / 2;
  graphics.fillStyle(0x0a0a30, 0.8);
  graphics.fillRect(pixelX - half, pixelY - half, TILE_SIZE, TILE_SIZE);
}

function createDotSprite(
  scene: Phaser.Scene,
  pixelX: number,
  pixelY: number,
  tileX: number,
  tileY: number,
): DotSprite {
  // Soft glow behind the dot
  const glow = scene.add.circle(pixelX, pixelY, 4, COLORS.DOT, 0.15);
  // Main dot with neon appearance
  const graphics = scene.add.circle(pixelX, pixelY, 2, COLORS.DOT, 0.85);
  return { graphics, glow, tileX, tileY, eaten: false };
}

function createPowerPillSprite(
  scene: Phaser.Scene,
  pixelX: number,
  pixelY: number,
  tileX: number,
  tileY: number,
): PowerPillSprite {
  // Outer glow circle - larger neon halo
  const glow = scene.add.circle(pixelX, pixelY, 12, COLORS.POWER_PILL_GLOW, 0.2);
  // Main power pill circle
  const graphics = scene.add.circle(pixelX, pixelY, 6, COLORS.POWER_PILL);
  return { graphics, glow, tileX, tileY, eaten: false };
}

function startPowerPillPulsing(
  scene: Phaser.Scene,
  powerPills: PowerPillSprite[],
): Phaser.Tweens.Tween[] {
  const tweens: Phaser.Tweens.Tween[] = [];

  for (const pill of powerPills) {
    // Animate the main pill: gentle scale pulse
    const pillTween = scene.tweens.add({
      targets: pill.graphics,
      alpha: { from: 1, to: 0.5 },
      scaleX: { from: 1, to: 0.8 },
      scaleY: { from: 1, to: 0.8 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    tweens.push(pillTween);

    // Animate the glow: expanding/contracting halo effect
    const glowTween = scene.tweens.add({
      targets: pill.glow,
      alpha: { from: 0.25, to: 0.06 },
      scaleX: { from: 1, to: 1.6 },
      scaleY: { from: 1, to: 1.6 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    tweens.push(glowTween);
  }

  return tweens;
}

function renderTunnelIndicators(
  _scene: Phaser.Scene,
  graphics: Phaser.GameObjects.Graphics,
  mapData: MapData,
): void {
  for (const tunnelPair of mapData.tunnelPairs) {
    renderTunnelOpening(graphics, tunnelPair.entry.x, tunnelPair.entry.y, mapData);
    renderTunnelOpening(graphics, tunnelPair.exit.x, tunnelPair.exit.y, mapData);
  }
}

function renderTunnelOpening(
  graphics: Phaser.GameObjects.Graphics,
  tileX: number,
  tileY: number,
  mapData: MapData,
): void {
  const pixelX = tileToPixelX(tileX);
  const pixelY = tileToPixelY(tileY);
  const half = TILE_SIZE / 2;

  // Subtle neon arrow indicator
  graphics.lineStyle(2, COLORS.MAZE_WALL, 0.4);

  const isLeftEdge = tileX === 0;
  const isRightEdge = tileX === mapData.width - 1;

  if (isLeftEdge) {
    graphics.beginPath();
    graphics.moveTo(pixelX + half, pixelY - 3);
    graphics.lineTo(pixelX - half + 2, pixelY);
    graphics.lineTo(pixelX + half, pixelY + 3);
    graphics.strokePath();
  } else if (isRightEdge) {
    graphics.beginPath();
    graphics.moveTo(pixelX - half, pixelY - 3);
    graphics.lineTo(pixelX + half - 2, pixelY);
    graphics.lineTo(pixelX - half, pixelY + 3);
    graphics.strokePath();
  }
}
